import React, { useState, useEffect } from 'react';
import { X, FolderPlus, Package, Eye, EyeOff, Shield, Globe, MapPin } from 'lucide-react';
import { useCategoryStore } from '../../stores/category-store';
import { Category, PermissionLevel, VisibilityType } from '../../types';
import { useUserStore } from '../../stores/user-store';
import { useI18n } from '../../i18n';
import { supabase } from '../../config/supabase';
// import { cn } from '../../utils/cn';

interface CreateCategoryModalProps {
  parentCategory?: Category;
  onClose: () => void;
}

const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({ parentCategory, onClose }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [visibilityType, setVisibilityType] = useState<VisibilityType>('private');
  // 订单绑定相关状态
  const [isOrderFolder, setIsOrderFolder] = useState<boolean>(false);
  const [orderNumber, setOrderNumber] = useState('');
  // 租户相关状态
  const [selectedTenant, setSelectedTenant] = useState<'cn' | 'intl'>('cn');
  const [canSelectTenant, setCanSelectTenant] = useState<boolean>(false);
  const [userTenant, setUserTenant] = useState<'cn' | 'intl'>('cn');

  // 当切换为订单文件夹时，自动设置为受限公开
  const handleOrderFolderToggle = (value: boolean) => {
    setIsOrderFolder(value);
    if (value) {
      setVisibilityType('restricted_public');
    }
  };
  const { createCategory, categories } = useCategoryStore();
  const { profile } = useUserStore();
  const { t } = useI18n();

  // 获取用户租户信息
  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        const { data: userProfile } = await supabase
          .from('v_fs_users')
          .select('tenant, can_access_all_tenants, role')
          .eq('id', user.id)
          .single();
        
        if (userProfile) {
          const tenant = userProfile.tenant || 'cn';
          setUserTenant(tenant);
          setSelectedTenant(tenant);
          // 超级管理员或有跨租户权限的用户可以选择租户
          setCanSelectTenant(userProfile.can_access_all_tenants || userProfile.role === 'super_admin');
        }
      } catch (error) {
        console.error('获取租户信息失败:', error);
      }
    };
    fetchTenantInfo();
  }, []);
  // 获取同级分类列表用于重名校验
  const siblingNames = React.useMemo(() => {
    const targetParentId = parentCategory?.id ?? null;
    const flat: any[] = [];
    const dfs = (arr: any[]) => { for (const c of arr) { flat.push(c); if (c.children?.length) dfs(c.children); } };
    dfs(categories as any[]);
    return flat
      .filter(c => (c.parent_id ?? null) === targetParentId)
      .map(c => (c.name || '').toLowerCase().trim());
  }, [categories, parentCategory?.id]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const trimmed = name.trim();
    const trimmedOrderNumber = orderNumber.trim();
    
    if (!trimmed) {
      setError(t('createCategory.errorRequired'));
      return;
    }
    if (siblingNames.includes(trimmed.toLowerCase())) {
      setError(t('createCategory.errorDuplicate'));
      return;
    }
    
    // 如果是订单文件夹，确定最终的订单号（如果没有填写则使用文件夹名）
    const finalOrderNumber = isOrderFolder ? (trimmedOrderNumber || trimmed) : null;
    
    // 如果是订单文件夹，检查并创建订单（如果不存在）
    if (isOrderFolder && finalOrderNumber) {
      try {
        const { data: orderExists, error: orderCheckError } = await supabase
          .from('orders')
          .select('id, order_number, is_active')
          .eq('order_number', finalOrderNumber)
          .eq('is_active', true)
          .single();
        
        // 如果订单不存在，自动创建
        if (orderCheckError && orderCheckError.code === 'PGRST116') {
          console.log(`订单 ${finalOrderNumber} 不存在，正在自动创建...`);
          
          const { data: newOrder, error: createOrderError } = await supabase
            .from('orders')
            .insert({
              order_number: finalOrderNumber,
              description: `自动创建的订单 - ${finalOrderNumber}`,
              folder_name: finalOrderNumber,
              created_by: profile?.id,
              is_active: true
            })
            .select()
            .single();
            
          if (createOrderError) {
            setError(`创建订单 ${finalOrderNumber} 失败: ${createOrderError.message}`);
            return;
          }
          
          console.log('订单创建成功:', newOrder);
        } else if (orderCheckError) {
          // 其他错误
          setError(`检查订单号时出错: ${orderCheckError.message}`);
          return;
        } else if (!orderExists.is_active) {
          setError(`订单号 ${finalOrderNumber} 已失效`);
          return;
        }
      } catch (orderError) {
        setError('处理订单号时出错，请重试');
        return;
      }
    }
    
    try {
      const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin' || profile?.role === 'uploader';
      
      // 创建分类，带上订单相关信息和租户信息
      const categoryData = await createCategory(trimmed, parentCategory?.id, {
        visibilityType: isAdmin ? visibilityType : 'private',
        minPermissionLevel: 'basic',
        isOrderFolder: isAdmin ? isOrderFolder : false,
        orderNumber: isAdmin && isOrderFolder && finalOrderNumber ? finalOrderNumber : null,
        tenant: selectedTenant // 新增：传入租户信息
      });
      
      // 如果是订单文件夹且提供了订单号，创建绑定关系
      if (isAdmin && isOrderFolder && finalOrderNumber && categoryData?.id) {
        try {
          const { error: bindingError } = await supabase
            .rpc('bind_category_to_order', {
              p_category_id: categoryData.id,
              p_order_number: finalOrderNumber,
              p_binding_type: 'manual',
              p_created_by: profile.id
            });
          
          if (bindingError) {
            console.warn('绑定订单失败:', bindingError);
            // 不中断流程，只是警告
          }
        } catch (bindError) {
          console.warn('绑定订单过程中出错:', bindError);
        }
      }
      
      onClose();
    } catch (error) {
      setError((error as Error).message);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-medium text-gray-800">
            {parentCategory ? t('createCategory.titleSub') : t('createCategory.titleRoot')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 rounded-full hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4">
          {error && (
            <div className="p-3 mb-4 text-sm text-red-600 bg-red-50 rounded-md">
              {error}
            </div>
          )}
          
          {parentCategory && (
            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <div className="text-sm text-gray-500">{t('createCategory.creatingUnder')}</div>
              <div className="text-sm font-medium text-gray-700">{parentCategory.name}</div>
            </div>
          )}
          
            <div className="mb-4">
            <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-700">
              {t('createCategory.nameLabel')}
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder={t('createCategory.namePlaceholder')}
            />
              {name.trim() && siblingNames.includes(name.trim().toLowerCase()) && (
                <p className="mt-1 text-xs text-red-600">{t('createCategory.duplicateNameHint')}</p>
              )}
          </div>
          
          {/* 订单绑定设置：管理员和上传管理员可见 */}
          {(profile?.role === 'admin' || profile?.role === 'super_admin' || profile?.role === 'uploader') && (
            <div className="mb-4 p-3 rounded-md border border-gray-200">
              <div className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Package className="w-4 h-4 mr-2 text-orange-600" />
                订单绑定设置
              </div>
              
              {/* 是否为订单文件夹 */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">创建为订单文件夹</span>
                <button
                  type="button"
                  onClick={() => handleOrderFolderToggle(!isOrderFolder)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full ${isOrderFolder ? 'bg-orange-600' : 'bg-gray-200'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isOrderFolder ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              
              {/* 订单号输入 */}
              {isOrderFolder && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    绑定订单号（可选）
                  </label>
                  <input
                    type="text"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="留空则使用文件夹名作为订单号"
                  />
                  <p className="text-xs text-gray-500">
                    💡 如果不填写，将使用文件夹名作为订单号；如果订单不存在，系统将自动创建
                  </p>
                </div>
              )}
            </div>
          )}

          {/* 可见性设置：管理员和上传管理员可见 */}
          {(profile?.role === 'admin' || profile?.role === 'super_admin' || profile?.role === 'uploader') && (
            <div className="mb-4 p-3 rounded-md border border-gray-200">
              <div className="text-sm font-medium text-gray-700 mb-3">可见性设置</div>
              <div className="space-y-3">
                {/* 私有选项 */}
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="visibility-private"
                    name="visibility"
                    value="private"
                    checked={visibilityType === 'private'}
                    onChange={(e) => setVisibilityType(e.target.value as VisibilityType)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                  />
                  <label htmlFor="visibility-private" className="ml-3 flex items-center cursor-pointer">
                    <EyeOff className="w-4 h-4 mr-2 text-gray-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-700">私有</div>
                      <div className="text-xs text-gray-500">只有创建者和被授权用户可见</div>
                    </div>
                  </label>
                </div>
                
                {/* 公开选项 */}
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="visibility-public"
                    name="visibility"
                    value="public"
                    checked={visibilityType === 'public'}
                    onChange={(e) => setVisibilityType(e.target.value as VisibilityType)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                  />
                  <label htmlFor="visibility-public" className="ml-3 flex items-center cursor-pointer">
                    <Eye className="w-4 h-4 mr-2 text-green-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-700">完全公开</div>
                      <div className="text-xs text-gray-500">所有用户都可以看到和访问</div>
                    </div>
                  </label>
                </div>
                
                {/* 受限公开选项 */}
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="visibility-restricted"
                    name="visibility"
                    value="restricted_public"
                    checked={visibilityType === 'restricted_public'}
                    onChange={(e) => setVisibilityType(e.target.value as VisibilityType)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                  />
                  <label htmlFor="visibility-restricted" className="ml-3 flex items-center cursor-pointer">
                    <Shield className="w-4 h-4 mr-2 text-orange-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-700">受限公开</div>
                      <div className="text-xs text-gray-500">默认隐藏，通过订单查询后可访问</div>
                    </div>
                  </label>
                </div>
                
                {/* 订单文件夹提示 */}
                {isOrderFolder && (
                  <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-md">
                    <div className="text-xs text-orange-700">
                      💡 订单文件夹建议选择"受限公开"，这样用户需要通过订单查询才能访问，提供了最佳的安全性和用户体验。
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 租户选择：管理员可见，只有超级管理员可以选择 */}
          {(profile?.role === 'admin' || profile?.role === 'super_admin' || profile?.role === 'uploader') && (
            <div className="mb-4 p-3 rounded-md border border-gray-200">
              <div className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Globe className="w-4 h-4 mr-2 text-blue-600" />
                区域设置
              </div>
              <div className="space-y-3">
                {/* 国内选项 */}
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="tenant-cn"
                    name="tenant"
                    value="cn"
                    checked={selectedTenant === 'cn'}
                    onChange={() => setSelectedTenant('cn')}
                    disabled={!canSelectTenant && userTenant !== 'cn'}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 disabled:opacity-50"
                  />
                  <label htmlFor="tenant-cn" className={`ml-3 flex items-center cursor-pointer ${!canSelectTenant && userTenant !== 'cn' ? 'opacity-50' : ''}`}>
                    <MapPin className="w-4 h-4 mr-2 text-red-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-700">国内 (CN)</div>
                      <div className="text-xs text-gray-500">中国大陆地区用户访问</div>
                    </div>
                  </label>
                </div>
                
                {/* 国际选项 */}
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="tenant-intl"
                    name="tenant"
                    value="intl"
                    checked={selectedTenant === 'intl'}
                    onChange={() => setSelectedTenant('intl')}
                    disabled={!canSelectTenant && userTenant !== 'intl'}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 disabled:opacity-50"
                  />
                  <label htmlFor="tenant-intl" className={`ml-3 flex items-center cursor-pointer ${!canSelectTenant && userTenant !== 'intl' ? 'opacity-50' : ''}`}>
                    <Globe className="w-4 h-4 mr-2 text-blue-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-700">国际 (INTL)</div>
                      <div className="text-xs text-gray-500">海外及港澳台地区用户访问</div>
                    </div>
                  </label>
                </div>

                {!canSelectTenant && (
                  <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="text-xs text-gray-600">
                      💡 您当前只能创建 <strong>{userTenant === 'cn' ? '国内' : '国际'}</strong> 区域的文件夹。如需创建其他区域文件夹，请联系超级管理员授权。
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('createCategory.cancel')}
            </button>
            <button
              type="submit"
              disabled={!name.trim() || siblingNames.includes(name.trim().toLowerCase())}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FolderPlus size={16} className="mr-2" />
              {t('createCategory.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCategoryModal;